import { useQuery } from "@tanstack/react-query";

import { readReleaseStatus } from "@/shared/api";
import type { ReleaseStatusResponse } from "@/shared/api";
import { statusOf } from "@/shared/http/errors";

export type ReleaseStatusState =
  | { readonly status: "loading" }
  | { readonly status: "forbidden" | "unavailable" | "unreadable" }
  | { readonly status: "ready"; readonly release: ReleaseStatusResponse };

/** One shared query for API/runtime facts; Web identity is reported separately. */
export function useReleaseStatus(): ReleaseStatusState {
  const query = useQuery({
    queryKey: ["production-release-status"],
    queryFn: async (): Promise<ReleaseStatusResponse> => {
      const { data, error, response } = await readReleaseStatus();
      if (error !== undefined) {
        throw Object.assign(new Error("release status unreadable"), {
          status: response?.status,
        });
      }
      return data;
    },
    refetchInterval: 60_000,
    retry: false,
  });

  if (query.isPending) return { status: "loading" };
  if (query.isError) {
    const responseStatus = statusOf(query.error);
    if (responseStatus === 401 || responseStatus === 403) return { status: "forbidden" };
    if (responseStatus === 503) return { status: "unavailable" };
    return { status: "unreadable" };
  }
  return { status: "ready", release: query.data };
}
